import classNames from "classnames";
import { PageSizes } from "../../config/pageSizes";
import { TablePaginationProps } from "./TablePagination.types";

const PAGE_SIZE_OPTIONS = PageSizes;

type PageEntry = number | "ellipsis";

const SIBLING_COUNT = 2;

/**
 * The page a table is on and the rows it covers, from a total that may be the server's
 * rather than the length of anything on screen. The one formula, so a table's slice and
 * its pagination cannot disagree about which page it is on.
 */
export function getPageRange(total: number, page: number, pageSize: number) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(1, page), pageCount);
  const from = (currentPage - 1) * pageSize;
  const to = Math.min(currentPage * pageSize, total);

  return {
    pageCount,
    currentPage,
    /** Slice bounds, zero-based and end-exclusive. */
    from,
    to,
    /** The same bounds as the reader counts them, from one. */
    rangeStart: total === 0 ? 0 : from + 1,
    rangeEnd: to,
  };
}

export function getPageEntries(
  currentPage: number,
  pageCount: number
): PageEntry[] {
  const siblings = Array.from(
    { length: SIBLING_COUNT * 2 + 1 },
    (_, index) => currentPage - SIBLING_COUNT + index
  );

  const pages = Array.from(new Set([1, pageCount, ...siblings]))
    .filter((page) => page >= 1 && page <= pageCount)
    .sort((a, b) => a - b);

  const entries: PageEntry[] = [];
  pages.forEach((page, index) => {
    if (index > 0 && page - pages[index - 1] > 1) {
      entries.push("ellipsis");
    }
    entries.push(page);
  });

  return entries;
}

/** Records-per-page, the range on screen, and the page keys. */
export function TablePagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeId = "table-page-size",
  showPageSize = true,
  className = "",
}: TablePaginationProps) {
  const { pageCount, currentPage, rangeStart, rangeEnd } = getPageRange(
    total,
    page,
    pageSize
  );

  return (
    <div className={classNames("page-table-pagination", className)}>
      <div className="page-table-pagination__summary">
        <span className="page-table-pagination__info">
          {total === 0
            ? "No records."
            : `Showing ${rangeStart}–${rangeEnd} of ${total}.`}
        </span>
        {showPageSize && (
          <div className="page-table-pagination__page-size-group">
            <label
              className="page-table-pagination__page-size-label"
              htmlFor={pageSizeId}
            >
              Records per page
            </label>
            <select
              id={pageSizeId}
              className="form-select page-table-pagination__page-size"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="page-table-pagination__controls">
        {getPageEntries(currentPage, pageCount).map((entry, index) =>
          entry === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="page-table-pagination__ellipsis"
            >
              &hellip;
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={classNames("page-table-pagination__page-btn", {
                "page-table-pagination__page-btn--active":
                  entry === currentPage,
              })}
              onClick={() => onPageChange(entry)}
            >
              {entry}
            </button>
          )
        )}
      </div>
    </div>
  );
}

export default TablePagination;
